import { EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { T } from '../../../../../Translations';
import LockedCoursesToggle from './LockedCoursesToggle';
import PreviewLicenseSelector from './PreviewLicenseSelector';
import UnlockedCount from './UnlockedCount';

// DVLP-7601: the preview-mode bar that replaces the Builder's blue stats bar.
// A light "mode banner" on the secondary surface with a primary top rule, so
// it reads as a state indicator rather than a second header: what you are
// looking at (Preview · Participant view), the one input that changes it
// (License) with its effect (N of M courses unlocked), the one view option
// (locked courses), and the way out. The collection name is not repeated
// here — it is the page's H1 just below.
//
// The preview opens in its own tab from the collections list, so "Close
// preview" closes that tab. Browsers only let a script close a tab it
// opened or one whose history holds a single entry; when the close is
// refused (a hand-opened tab, or a login bounce in the history) we fall
// back to navigating to the list instead of leaving a dead button.

function Divider() {
  return <span aria-hidden="true" className="h-6 w-px bg-border flex-shrink-0" />;
}

function PreviewBar() {
  const navigate = useNavigate();
  const closePreview = () => {
    window.close();
    window.setTimeout(() => {
      if (!window.closed) navigate('/learning_collections');
    }, 150);
  };

  return (
    <div
      className="bg-secondary text-secondary-foreground border-t-2 border-primary border-b border-border"
      data-intercom-target="preview-bar"
    >
      <div
        className="max-w-clientPortalMaxWidth mx-auto px-6 md:px-14 py-3"
        data-intercom-target="preview-bar-content"
      >
        <div className="flex items-center justify-between gap-6">
          <div
            className="flex items-center gap-4 min-w-0"
            data-intercom-target="preview-bar-license-group"
          >
            <span
              className="flex items-center gap-2 flex-shrink-0"
              data-intercom-target="preview-mode-label"
            >
              <EyeIcon className="w-5 h-5" aria-hidden="true" />
              <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                <T kind="Preview" />
              </span>
              <span aria-hidden="true" className="text-primary/50">
                ·
              </span>
              <T kind="Participant view" />
            </span>
            <Divider />
            <span className="flex-shrink-0" data-intercom-target="preview-license-label">
              <T kind="License" />
            </span>
            <PreviewLicenseSelector />
            <UnlockedCount />
          </div>

          <div
            className="flex items-center gap-4 flex-shrink-0"
            data-intercom-target="preview-bar-actions"
          >
            <LockedCoursesToggle />
            <Divider />
            <button
              type="button"
              onClick={closePreview}
              className="flex items-center gap-2 text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
              style={{ fontWeight: 'var(--font-weight-semibold)' }}
              data-intercom-target="preview-close-button"
            >
              <XMarkIcon className="w-4 h-4" aria-hidden="true" />
              <T kind="Close preview" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreviewBar;
